import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import TouchTooltip from '../../common/TouchTooltip';
import useContentWidth from './useContentWidth';
import { formatDate, fromYmd } from '../../../utils/buyerInsightsDates';
import { formatDemandTooltip, formatPriceBandTooltip, formatRunTooltip } from '../../../utils/buyerInsightsFormat';

// Desktop rendering of the timeline (spec §3): one SVG, one shared x-scale, lanes top to bottom —
// month ticks · product markers · Matches shown per day · Price · one lane per size. Facts only:
// no totals, no partner names, no amounts.

const LABEL_W = 120;
const PAD_R = 12;
const ROW = { ticks: 20, product: 26, demand: 64, price: 34, size: 32 };
const COLORS = {
  available: '#e3f2fd',
  availableEdge: '#90caf9',
  soldOut: '#eeeeee',
  soldOutEdge: '#e0e0e0',
  fullPrice: '#f5f5f5',
  discount: '#fff3e0',
  discountEdge: '#ffb74d',
  discountText: '#e65100',
  bar: '#90caf9',
  tick: '#e0e0e0',
  label: '#616161',
  firstSeen: '#757575',
  firstMatched: '#1976d2',
  partnerSoldOut: '#ef6c00',
  partnerBack: '#1976d2',
  partnerOrder: '#c62828',
};

const dayIndex = (ymd, start) => Math.round((fromYmd(ymd) - fromYmd(start)) / 86400000);

// MUI Tooltip needs a child that takes a ref and mouse events; an SVG <g> does both.
const Hover = React.forwardRef(({ children, ...props }, ref) => (
  <g ref={ref} {...props}>
    {children}
  </g>
));

const Tip = ({ title, children }) => (
  <TouchTooltip title={title}>
    <Hover>{children}</Hover>
  </TouchTooltip>
);

const Marker = ({ kind, x, y, tip }) => {
  const c = {
    first_seen: COLORS.firstSeen,
    first_matched: COLORS.firstMatched,
    partner_sold_out: COLORS.partnerSoldOut,
    partner_back_in_stock: COLORS.partnerBack,
    partner_order: COLORS.partnerOrder,
  }[kind];
  let shape;
  switch (kind) {
    case 'first_seen':
      shape = <circle cx={x} cy={y} r={4.5} fill="#fff" stroke={c} strokeWidth={2} />;
      break;
    case 'first_matched':
      shape = <polygon points={`${x},${y - 5.5} ${x + 5.5},${y} ${x},${y + 5.5} ${x - 5.5},${y}`} fill={c} />;
      break;
    case 'partner_sold_out':
      shape = <polygon points={`${x},${y - 5} ${x + 5},${y + 4} ${x - 5},${y + 4}`} fill={c} />;
      break;
    case 'partner_back_in_stock':
      shape = <polygon points={`${x},${y + 5} ${x + 5},${y - 4} ${x - 5},${y - 4}`} fill="#fff" stroke={c} strokeWidth={1.5} />;
      break;
    default:
      shape = <circle cx={x} cy={y} r={4.5} fill={c} />;
  }
  return (
    <Tip title={tip}>
      <circle cx={x} cy={y} r={9} fill="transparent" />
      {shape}
    </Tip>
  );
};

const LEGEND = [
  { kind: 'available', label: 'Available' },
  { kind: 'sold_out', label: 'Sold out' },
  { kind: 'first_seen', label: 'First seen' },
  { kind: 'first_matched', label: 'First matched on the network' },
  { kind: 'partner_sold_out', label: 'A partner sold out' },
  { kind: 'partner_back_in_stock', label: 'A partner back in stock' },
  { kind: 'partner_order', label: 'Ordered at a partner while you were sold out' },
];

const LegendSwatch = ({ kind }) => {
  if (kind === 'available' || kind === 'sold_out') {
    const fill = kind === 'available' ? COLORS.available : COLORS.soldOut;
    const edge = kind === 'available' ? COLORS.availableEdge : COLORS.soldOutEdge;
    return <rect x={1} y={4} width={16} height={10} rx={2} fill={fill} stroke={edge} />;
  }
  return <Marker kind={kind} x={9} y={9} tip="" />;
};

const TimelineLanes = ({ data, minWidth = 0 }) => {
  const ref = useRef(null);
  const measured = useContentWidth(ref);
  const width = Math.max(measured || 640, minWidth);
  const { start, end } = data.window;
  const days = dayIndex(end, start) + 1;
  const x0 = LABEL_W;
  const x1 = width - PAD_R;
  const dayW = (x1 - x0) / days;
  const x = (ymd) => x0 + dayIndex(ymd, start) * dayW;
  const xEnd = (ymd) => (ymd ? x(ymd) + dayW : x1); // right edge of a run: its `to` day inclusive, or End

  const sizes = data.sizes || [];
  const height = ROW.ticks + ROW.product + ROW.demand + ROW.price + sizes.length * ROW.size + 4;
  let y = ROW.ticks;
  const yProduct = y;
  y += ROW.product;
  const yDemand = y;
  y += ROW.demand;
  const yPrice = y;
  y += ROW.price;
  const ySize = (i) => y + i * ROW.size;

  // Month ticks: the first of each month inside the window, plus Start.
  const ticks = [];
  const d0 = fromYmd(start);
  const d1 = fromYmd(end);
  for (let d = new Date(d0.getFullYear(), d0.getMonth() + 1, 1); d <= d1; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
    const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    ticks.push(ymd);
  }

  const maxMatches = Math.max(1, ...(data.demand || []).map((d) => d.matches_shown));
  const hatchTo = data.first_seen > start ? x(data.first_seen) : null;

  const laneLabel = (text, yTop, h, sub) => (
    <>
      <text x={0} y={yTop + h / 2 + (sub ? -2 : 4)} fontSize={12} fontWeight={600} fill={COLORS.label}>
        {text}
      </text>
      {sub && (
        <text x={0} y={yTop + h / 2 + 11} fontSize={10} fill={COLORS.label}>
          {sub}
        </text>
      )}
    </>
  );

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <svg width={width} height={height} role="img" aria-label="Product timeline" style={{ display: 'block', fontFamily: 'inherit' }}>
        <defs>
          <pattern id="bi-hatch" width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1={0} y1={0} x2={0} y2={6} stroke="#d0d0d0" strokeWidth={2} />
          </pattern>
        </defs>

        {/* Before first seen: hatched across every lane */}
        {hatchTo !== null && (
          <Tip title={`Before first seen (${formatDate(data.first_seen)})`}>
            <rect x={x0} y={ROW.ticks} width={Math.max(0, hatchTo - x0)} height={height - ROW.ticks} fill="url(#bi-hatch)" opacity={0.6} />
          </Tip>
        )}

        {/* Ticks */}
        <text x={x0} y={13} fontSize={11} fill={COLORS.label}>
          {formatDate(start)}
        </text>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={x(t)} y1={ROW.ticks - 4} x2={x(t)} y2={height} stroke={COLORS.tick} />
            <text x={x(t) + 3} y={13} fontSize={11} fill={COLORS.label}>
              {formatDate(t)}
            </text>
          </g>
        ))}
        <text x={x1} y={13} fontSize={11} fill={COLORS.label} textAnchor="end">
          {formatDate(end)}
        </text>

        {/* Product markers */}
        {laneLabel('Product', yProduct, ROW.product)}
        <line x1={x0} y1={yProduct + ROW.product / 2} x2={x1} y2={yProduct + ROW.product / 2} stroke={COLORS.tick} />
        {data.first_seen >= start && (
          <Marker kind="first_seen" x={x(data.first_seen) + dayW / 2} y={yProduct + ROW.product / 2} tip={`First seen ${formatDate(data.first_seen)}`} />
        )}
        {data.first_matched && data.first_matched >= start && (
          <Marker
            kind="first_matched"
            x={x(data.first_matched) + dayW / 2}
            y={yProduct + ROW.product / 2}
            tip={`First matched on the network ${formatDate(data.first_matched)}`}
          />
        )}

        {/* Matches shown per day */}
        {laneLabel('Matches shown', yDemand, ROW.demand, 'per day')}
        <line x1={x0} y1={yDemand + ROW.demand - 6} x2={x1} y2={yDemand + ROW.demand - 6} stroke={COLORS.tick} />
        {(data.demand || []).map((d) => {
          const h = Math.max(1, ((ROW.demand - 14) * d.matches_shown) / maxMatches);
          return (
            <Tip key={d.day} title={formatDemandTooltip(d)}>
              <rect x={x(d.day)} y={yDemand + 4} width={Math.max(dayW, 2)} height={ROW.demand - 10} fill="transparent" />
              <rect x={x(d.day) + 0.5} y={yDemand + ROW.demand - 6 - h} width={Math.max(dayW - 1, 1.5)} height={h} fill={COLORS.bar} />
            </Tip>
          );
        })}

        {/* Price */}
        {laneLabel('Price', yPrice, ROW.price)}
        {(data.price || []).map((b) => {
          const bx = x(b.from);
          const bw = Math.max(1, xEnd(b.to) - bx);
          return (
            <Tip key={b.from} title={formatPriceBandTooltip(b, end)}>
              <rect
                x={bx}
                y={yPrice + 6}
                width={bw}
                height={ROW.price - 12}
                rx={3}
                fill={b.discounted ? COLORS.discount : COLORS.fullPrice}
                stroke={b.discounted ? COLORS.discountEdge : COLORS.soldOutEdge}
              />
              {bw > 34 && (
                <text x={bx + 6} y={yPrice + ROW.price / 2 + 4} fontSize={11} fontWeight={600} fill={b.discounted ? COLORS.discountText : COLORS.label}>
                  {b.discounted ? (b.pct_off ? `−${b.pct_off}%` : 'On sale') : 'Full price'}
                </text>
              )}
            </Tip>
          );
        })}

        {/* Sizes */}
        {sizes.map((s, i) => {
          const yt = ySize(i);
          const cy = yt + ROW.size / 2;
          return (
            <g key={s.size_id}>
              {laneLabel(`Size ${s.label}`, yt, ROW.size)}
              {s.first_seen > start && (
                <rect x={x0} y={yt + 7} width={Math.max(0, x(s.first_seen) - x0)} height={ROW.size - 14} fill="url(#bi-hatch)" opacity={0.6} />
              )}
              {(s.runs || []).map((r) => {
                const rx = x(r.from);
                const rw = Math.max(1, xEnd(r.to) - rx);
                return (
                  <Tip key={r.from} title={formatRunTooltip(r, end)}>
                    <rect
                      x={rx}
                      y={yt + 7}
                      width={rw}
                      height={ROW.size - 14}
                      rx={3}
                      fill={r.in_stock ? COLORS.available : COLORS.soldOut}
                      stroke={r.in_stock ? COLORS.availableEdge : COLORS.soldOutEdge}
                    />
                  </Tip>
                );
              })}
              {(s.partner_events || []).map((e) => (
                <Marker
                  key={`${e.day}-${e.kind}`}
                  kind={`partner_${e.kind}`}
                  x={x(e.day) + dayW / 2}
                  y={cy}
                  tip={`${formatDate(e.day)}: ${e.partners === 1 ? 'a partner' : `${e.partners} partners`} ${e.kind === 'sold_out' ? 'sold out' : 'back in stock'}`}
                />
              ))}
              {(s.partner_orders || []).map((o) => (
                <Marker
                  key={`o-${o.day}`}
                  kind="partner_order"
                  x={x(o.day) + dayW / 2}
                  y={cy}
                  tip={`${formatDate(o.day)}: ${o.orders === 1 ? 'a shopper you sent' : `${o.orders} shoppers you sent`} ordered it at a partner while you were sold out`}
                />
              ))}
            </g>
          );
        })}
      </svg>

      {/* Legend: exactly the states and the five markers */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
        {LEGEND.map((l) => (
          <Box key={l.kind} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <svg width={18} height={18} aria-hidden="true">
              <LegendSwatch kind={l.kind} />
            </svg>
            <Typography variant="caption" color="text.secondary">
              {l.label}
            </Typography>
          </Box>
        ))}
        {hatchTo !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <svg width={18} height={18} aria-hidden="true">
              <rect x={1} y={4} width={16} height={10} fill="url(#bi-hatch)" opacity={0.6} stroke="#d0d0d0" />
            </svg>
            <Typography variant="caption" color="text.secondary">
              Before first seen ({formatDate(data.first_seen)})
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TimelineLanes;
